import React, { useEffect, useState, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    Paper,
    IconButton,
    useTheme,
    alpha,
    Tooltip,
    Chip,
    CircularProgress,
    Grid,
    Fade,
    Zoom,
    Collapse,
    List,
    ListItem,
    ListItemText,
    Divider,
    Alert,
    AlertTitle
} from '@mui/material';
import { AgreementEntity, RewardEntity, CalendarEntryEntity, CalendarEntryEntityTypeEnum, PaymentEntity } from 'api/generated';
import {
    agreementApi, employeePaymentsApi, calendarEntriesApi
} from 'api';
import { getDecryptedNetDaily, fetchCurrentPaymentData, getDecryptedPayment } from 'pages/employees/reportUtils';
import { isSameDay, isBefore, isAfter } from 'date-fns';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import EuroIcon from '@mui/icons-material/Euro';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PaymentsIcon from '@mui/icons-material/Payments';
import EventIcon from '@mui/icons-material/Event';
import NoteIcon from '@mui/icons-material/Note';
import LockIcon from '@mui/icons-material/Lock';
import { useTranslation } from 'react-i18next';
import { useEncryption } from 'contexts/EncryptionKeyContext';
import { encryptData, getDecryptedSpecialDailyNet, decryptData } from 'utils/encryptionDecryptionAgreements';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

interface EmployeeCalendarDayPopupProps {
    open: boolean;
    onClose: () => void;
    date: Date | null;
    employeeId: number;
    seasonId: number | null;
    contractId: number;
    rewards: RewardEntity[];
    onDataChange?: () => void;
}

const EmployeeCalendarDayPopup: React.FC<EmployeeCalendarDayPopupProps> = ({
    open, onClose, date, employeeId, seasonId, contractId, rewards, onDataChange
}) => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const { encryptionKey, setPopoverOpen } = useEncryption();
    const [agreements, setAgreements] = useState<AgreementEntity[]>([]);
    const [dailyNet, setDailyNet] = useState<number | null>(null);
    const [specialNetId, setSpecialNetId] = useState<number | null>(null);
    const [notes, setNotes] = useState<string>('');
    const [cumulative, setCumulative] = useState<number | null>(null);
    const [payments, setPayments] = useState<PaymentEntity[]>([]);
    const [totalPayout, setTotalPayout] = useState<number>(0);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [calendarEvent, setCalendarEvent] = useState<CalendarEntryEntity | null>(null);
    const [showEncryptionAlert, setShowEncryptionAlert] = useState(false);
    const [isSpecialNetEncrypted, setIsSpecialNetEncrypted] = useState(false);
    const [balance730, setBalance730] = useState<number>(0);
    const [balance730Date, setBalance730Date] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        if (!date || !employeeId) return;

        setLoading(true);
        try {
            // Fetch agreements and payments
            const agreementsData = (await agreementApi.findAll(employeeId, seasonId ?? undefined, contractId ?? undefined)).data;
            setAgreements(agreementsData);

            const paymentsData = (await employeePaymentsApi.findAll(employeeId, seasonId ?? 0)).data;

            // Filter payments for the current day
            const paymentsForDay = paymentsData.filter((p: any) => isSameDay(new Date(p.paymentDate), date));
            setPayments(paymentsForDay);

            // Calculate total payout for the day
            const total = paymentsForDay.reduce((sum: number, payment: any) => {
                return sum + getDecryptedPayment(payment, encryptionKey);
            }, 0);
            setTotalPayout(total);

            // Fetch calendar events
            const calendarEvents = (await calendarEntriesApi.findAll(employeeId.toString(), seasonId ?? 0)).data;
            const eventForDay = calendarEvents.find((event) =>
                isSameDay(new Date(event.startedOn), date) ||
                (isSameDay(new Date(event.endedOn), date) ||
                    (new Date(event.startedOn) <= date && new Date(event.endedOn) >= date))
            );
            setCalendarEvent(eventForDay || null);

            // First check for special daily net across all agreements
            let net = 0;
            let specialNetId: number | null = null;
            let specialNetNotes = '';
            let foundSpecialNet = false;
            let isSpecialNetEncrypted = false;

            for (const agreement of agreementsData) {
                if (agreement.SpecialDailyNet) {
                    const specialNet = agreement.SpecialDailyNet.find((sdn: any) => isSameDay(new Date(sdn.date), date));
                    if (specialNet) {
                        net = getDecryptedSpecialDailyNet(specialNet, encryptionKey);
                        specialNetId = specialNet.id;
                        specialNetNotes = specialNet.notes || '';
                        isSpecialNetEncrypted = !!(specialNet.net_encrypted && encryptionKey && encryptionKey !== '');
                        foundSpecialNet = true;
                        break;
                    }
                }
            }

            // If no special net found, find the applicable agreement for regular net daily
            if (!foundSpecialNet) {
                const applicableAgreement = agreementsData.find(agreement =>
                    (isBefore(new Date(agreement.startedOn), date) || isSameDay(new Date(agreement.startedOn), date)) &&
                    (!agreement.endedOn || isAfter(new Date(agreement.endedOn), date) || isSameDay(new Date(agreement.endedOn), date))
                );

                if (applicableAgreement) {
                    net = getDecryptedNetDaily(applicableAgreement, encryptionKey);
                }
            }

            setDailyNet(net);
            setSpecialNetId(specialNetId);
            setNotes(specialNetNotes);
            setIsSpecialNetEncrypted(isSpecialNetEncrypted);

            // Calculate 730 balance for this date
            const applicableAgreementFor730 = agreementsData
                .filter(agreement => {
                    // Check if agreement has 730 balance
                    const hasBalance = encryptionKey && encryptionKey !== ''
                        ? agreement.balance730_encrypted && agreement.balance730_encrypted !== ''
                        : agreement.balance730 !== null && agreement.balance730 !== 0;
                    
                    if (!hasBalance) return false;

                    // Check if the agreement is active on this date
                    const startDate = new Date(agreement.startedOn);
                    const endDate = agreement.endedOn ? new Date(agreement.endedOn) : null;
                    const isActiveOnDate = (isBefore(startDate, date) || isSameDay(startDate, date)) &&
                           (!endDate || isAfter(endDate, date) || isSameDay(endDate, date));

                    if (!isActiveOnDate) return false;

                    // Check if 730 balance date is on or before this date (carries forward)
                    const balance730Date = agreement.balance730_date ? new Date(agreement.balance730_date) : null;
                    return !balance730Date || balance730Date <= date;
                })
                .sort((a, b) => {
                    const dateA = a.balance730_date ? new Date(a.balance730_date) : new Date(0);
                    const dateB = b.balance730_date ? new Date(b.balance730_date) : new Date(0);
                    return dateB.getTime() - dateA.getTime();
                });

            if (applicableAgreementFor730.length > 0) {
                const agreementWithBalance = applicableAgreementFor730[0];
                const balance = encryptionKey && encryptionKey !== '' && agreementWithBalance.balance730_encrypted
                    ? Number(decryptData(agreementWithBalance.balance730_encrypted, encryptionKey))
                    : agreementWithBalance.balance730 || 0;
                setBalance730(balance);
                setBalance730Date(agreementWithBalance.balance730_date);
            } else {
                setBalance730(0);
                setBalance730Date(null);
            }

            // Cumulative total up to this day using fetchCurrentPaymentData
            const paymentData = await fetchCurrentPaymentData(employeeId, seasonId, contractId, encryptionKey, date);
            setCumulative(
                paymentData
                    ? (paymentData.totalEarned ?? 0) - (paymentData.totalPaid ?? 0)
                    : null
            );
        } finally {
            setLoading(false);
        }
    }, [date, employeeId, seasonId, contractId, encryptionKey]);

    // Reset state when modal closes
    useEffect(() => {
        if (!open) {
            setEditing(false);
            setSaving(false);
            setDailyNet(null);
            setSpecialNetId(null);
            setNotes('');
            setCumulative(null);
            setPayments([]);
            setTotalPayout(0);
            setShowEncryptionAlert(false);
            setIsSpecialNetEncrypted(false);
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            fetchData();
        }
    }, [open, fetchData]);

    // Handler for saving the daily net and notes
    const handleSaveDailyNet = async () => {
        if (!agreements[0] || !date || dailyNet === null) return;

        // Check if encryption key is available
        if (!encryptionKey || encryptionKey === '') {
            setShowEncryptionAlert(true);
            return;
        }

        setSaving(true);
        try {
            const dateString = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
            const encryptedNet = encryptData(dailyNet, encryptionKey);

            if (!encryptedNet) {
                throw new Error('Failed to encrypt data');
            }

            if (specialNetId) {
                // Update existing special daily net
                await agreementApi.updateSpecialDailyNet(specialNetId.toString(), {
                    net_encrypted: encryptedNet,
                    date: dateString,
                    notes
                } as any);
            } else {
                // Create new special daily net
                await agreementApi.createSpecialDailyNet({
                    employeeId,
                    agreementId: agreements[0].id,
                    date: dateString,
                    net_encrypted: encryptedNet,
                    notes
                } as any);
            }
            setEditing(false);
            setShowEncryptionAlert(false);
            await fetchData(); // Refresh data immediately after saving
            onDataChange?.();
        } catch (error) {
            console.error('Error saving daily net:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        onDataChange?.();
        onClose();
    };

    const formatDate = (date: Date) => {
        return date.toLocaleDateString(i18n.language, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                    overflow: 'hidden'
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pb: 2,
                pt: 3,
                px: 4,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`
            }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {t('employees.calendar.dayDetails')}
                </Typography>
                <IconButton
                    onClick={handleClose}
                    size="small"
                    sx={{
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            transform: 'rotate(90deg)',
                            backgroundColor: alpha(theme.palette.error.main, 0.1)
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 4 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                        <CircularProgress size={40} thickness={4} />
                    </Box>
                ) : (
                    <Fade in={!loading} timeout={500}>
                        <Box>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    mb: 4,
                                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                                    borderRadius: 3,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                    transition: 'all 0.3s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
                                    }
                                }}
                            >
                                <Typography variant="h6" color="primary" sx={{
                                    mb: 1,
                                    fontWeight: 600,
                                    letterSpacing: '0.5px'
                                }}>
                                    {date ? formatDate(date) : ''}
                                </Typography>
                            </Paper>

                            {showEncryptionAlert && (
                                <Alert 
                                    severity="warning" 
                                    sx={{ mb: 3 }}
                                    icon={<LockIcon />}
                                    action={
                                        <Button
                                            color="inherit"
                                            size="small"
                                            onClick={() => setPopoverOpen(true)}
                                            sx={{ fontWeight: 600 }}
                                        >
                                            {t('encryption.setKey')}
                                        </Button>
                                    }
                                >
                                    <AlertTitle sx={{ fontWeight: 600 }}>
                                        {t('encryption.keyRequired')}
                                    </AlertTitle>
                                    {t('encryption.specialNetKeyRequired')}
                                </Alert>
                            )}

                            {calendarEvent && (
                                <Zoom in={!!calendarEvent} timeout={500}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            mb: 4,
                                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                            borderRadius: 3,
                                            transition: 'all 0.3s ease-in-out',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <EventIcon sx={{
                                                mr: 1.5,
                                                color: 'primary.main',
                                                fontSize: 28
                                            }} />
                                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                {t('employees.calendar.events')}
                                            </Typography>
                                        </Box>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12}>
                                                <Chip
                                                    label={t(`employees.calendar.types.${calendarEvent.type.toLowerCase()}`)}
                                                    color={calendarEvent.type === CalendarEntryEntityTypeEnum.UnpaidLeave ? 'error' : 'primary'}
                                                    sx={{
                                                        mb: 2,
                                                        height: 32,
                                                        '& .MuiChip-label': {
                                                            px: 2,
                                                            fontWeight: 600
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                    {t('employees.calendar.entry.startedOn')}
                                                </Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                    {formatDate(new Date(calendarEvent.startedOn))}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                    {t('employees.calendar.entry.endedOn')}
                                                </Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                    {formatDate(new Date(calendarEvent.endedOn))}
                                                </Typography>
                                            </Grid>
                                            {calendarEvent.accepted && (
                                                <Grid item xs={12}>
                                                    <Chip
                                                        label={t('employees.calendar.entry.accepted')}
                                                        color="success"
                                                        size="small"
                                                        sx={{
                                                            '& .MuiChip-label': {
                                                                fontWeight: 600
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Paper>
                                </Zoom>
                            )}

                            <Box sx={{ display: 'grid', gap: 4 }}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                        borderRadius: 3,
                                        transition: 'all 0.3s ease-in-out',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <EuroIcon sx={{
                                            mr: 1.5,
                                            color: 'primary.main',
                                            fontSize: 28
                                        }} />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {t('employees.agreements.field.net')}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        {editing ? (
                                            <TextField
                                                size="small"
                                                type="number"
                                                value={dailyNet ?? ''}
                                                onChange={(e) => { setDailyNet(Number(e.target.value)); }}
                                                sx={{
                                                    width: 120,
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                    }
                                                }}
                                                disabled={saving}
                                            />
                                        ) : (
                                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                                {dailyNet !== null ? `${dailyNet.toFixed(2).replace('.', ',')}€` : 'N/A'}
                                            </Typography>
                                        )}

                                        <Chip
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    {specialNetId && isSpecialNetEncrypted && (
                                                        <LockIcon sx={{ fontSize: 12, color: 'inherit' }} />
                                                    )}
                                                    {specialNetId ? t('employees.calendar.specialRate') : t('employees.calendar.standardRate')}
                                                </Box>
                                            }
                                            size="small"
                                            color={specialNetId ? 'warning' : 'default'}
                                            sx={{
                                                borderRadius: 2,
                                                '& .MuiChip-label': {
                                                    fontWeight: 600
                                                }
                                            }}
                                        />

                                        <Tooltip title={editing ? t('save') : t('common.forms.button.edit')}>
                                            <IconButton
                                                color={editing ? 'primary' : 'default'}
                                                onClick={editing ? handleSaveDailyNet : () => { setEditing(true); }}
                                                disabled={saving}
                                                sx={{
                                                    ml: 'auto',
                                                    transition: 'all 0.2s ease-in-out',
                                                    '&:hover': {
                                                        transform: 'scale(1.1)',
                                                        backgroundColor: alpha(theme.palette.primary.main, 0.1)
                                                    }
                                                }}
                                            >
                                                {saving ? <CircularProgress size={24} /> : editing ? <SaveIcon /> : <EditIcon />}
                                            </IconButton>
                                        </Tooltip>
                                    </Box>

                                    <Collapse in={specialNetId !== null || editing}>
                                        <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <NoteIcon sx={{
                                                    mr: 1,
                                                    color: 'text.secondary',
                                                    fontSize: 20
                                                }} />
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    {t('notes.collectionName')}
                                                </Typography>
                                                {!editing && notes && (
                                                    <Tooltip title={t('common.forms.button.edit')}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setEditing(true);
                                                            }}
                                                            sx={{
                                                                ml: 'auto',
                                                                transition: 'all 0.2s ease-in-out',
                                                                '&:hover': {
                                                                    transform: 'scale(1.1)',
                                                                    backgroundColor: alpha(theme.palette.primary.main, 0.1)
                                                                }
                                                            }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                            {editing ? (
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    value={notes}
                                                    onChange={(e) => { setNotes(e.target.value) }}
                                                    placeholder={t('employees.calendar.notesPlaceholder')}
                                                    disabled={saving}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                            transition: 'all 0.2s ease-in-out',
                                                            '&:hover': {
                                                                backgroundColor: alpha(theme.palette.primary.main, 0.02)
                                                            }
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        p: 2,
                                                        borderRadius: 2,
                                                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                                                        minHeight: '80px',
                                                        whiteSpace: 'pre-wrap'
                                                    }}
                                                >
                                                    {notes || t('employees.calendar.noNotes')}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Collapse>
                                </Paper>

                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                        borderRadius: 3,
                                        transition: 'all 0.3s ease-in-out',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <TrendingUpIcon sx={{
                                            mr: 1.5,
                                            color: 'success.main',
                                            fontSize: 28
                                        }} />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {t('report.cumulative')}
                                        </Typography>
                                    </Box>
                                    <Typography variant="h5" sx={{
                                        fontWeight: 600,
                                        color: 'success.main',
                                        letterSpacing: '0.5px'
                                    }}>
                                        {cumulative !== null ? `${cumulative.toFixed(2).replace('.', ',')}€` : 'N/A'}
                                    </Typography>
                                </Paper>

                                {/* 730 Balance Section */}
                                {balance730 !== 0 && (
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                            borderRadius: 3,
                                            transition: 'all 0.3s ease-in-out',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <AccountBalanceIcon sx={{
                                                mr: 1.5,
                                                color: balance730 > 0 ? 'warning.main' : 'error.main',
                                                fontSize: 28
                                            }} />
                                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                {balance730 > 0 ? t('employees.report.balance730.credit') : t('employees.report.balance730.debt')}
                                            </Typography>
                                        </Box>
                                        <Typography variant="h5" sx={{
                                            fontWeight: 600,
                                            color: balance730 > 0 ? 'warning.main' : 'error.main',
                                            letterSpacing: '0.5px'
                                        }}>
                                            {balance730.toFixed(2).replace('.', ',')}€
                                        </Typography>
                                        {balance730Date && (
                                            <Typography variant="body2" sx={{
                                                mt: 1,
                                                color: 'text.secondary'
                                            }}>
                                                {t('employees.report.balance730.appliedOn')}: {new Date(balance730Date).toLocaleDateString('de-DE')}
                                            </Typography>
                                        )}
                                    </Paper>
                                )}

                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                        borderRadius: 3,
                                        transition: 'all 0.3s ease-in-out',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <PaymentsIcon sx={{
                                            mr: 1.5,
                                            color: 'info.main',
                                            fontSize: 28
                                        }} />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {t('employees.calendar.todaysPayout')}
                                        </Typography>
                                    </Box>

                                    {payments.length > 0 ? (
                                        <>
                                            <List sx={{
                                                width: '100%',
                                                bgcolor: alpha(theme.palette.primary.main, 0.02),
                                                borderRadius: 2,
                                                mb: 2
                                            }}>
                                                {payments.map((payment, index) => (
                                                    <React.Fragment key={payment.id}>
                                                        <ListItem
                                                            sx={{
                                                                py: 1.5,
                                                                px: 2,
                                                                '&:hover': {
                                                                    bgcolor: alpha(theme.palette.primary.main, 0.04)
                                                                }
                                                            }}
                                                        >
                                                            <ListItemText
                                                                primary={
                                                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                                        {new Date(payment.paymentDate).toLocaleTimeString(i18n.language, {
                                                                            hour: '2-digit',
                                                                            minute: '2-digit'
                                                                        })}
                                                                    </Typography>
                                                                }
                                                                secondary={
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        {t('employees.generalInformation.lastPayment')}
                                                                    </Typography>
                                                                }
                                                            />
                                                            <Typography
                                                                variant="body1"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: 'info.main',
                                                                    ml: 2
                                                                }}
                                                            >
                                                                {getDecryptedPayment(payment, encryptionKey).toFixed(2)}€
                                                            </Typography>
                                                        </ListItem>
                                                        {index < payments.length - 1 && (
                                                            <Divider
                                                                component="li"
                                                                sx={{
                                                                    borderColor: alpha(theme.palette.divider, 0.1),
                                                                    mx: 2
                                                                }}
                                                            />
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </List>
                                            <Box sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                mt: 2,
                                                pt: 2,
                                                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                                            }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                    {t('employees.calendar.totalPayout')}
                                                </Typography>
                                                <Typography
                                                    variant="h5"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: 'info.main',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    {totalPayout.toFixed(2)}€
                                                </Typography>
                                            </Box>
                                        </>
                                    ) : (
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                p: 2,
                                                textAlign: 'center',
                                                color: 'text.secondary',
                                                bgcolor: alpha(theme.palette.primary.main, 0.02),
                                                borderRadius: 2
                                            }}
                                        >
                                            {t('employees.calendar.noPayout')}
                                        </Typography>
                                    )}
                                </Paper>
                            </Box>
                        </Box>
                    </Fade>
                )}
            </DialogContent>

            <DialogActions sx={{
                px: 4,
                py: 3,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`
            }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        px: 4,
                        py: 1,
                        fontWeight: 600,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        }
                    }}
                >
                    {t('dialog.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmployeeCalendarDayPopup; 